/**
 * Filter sidebar for the Rules Library. Status / severity / rule-type are
 * fixed checkbox lists; domains and tags are autocompleted from the values
 * present in the loaded rule set so the sidebar shows only options that
 * exist in the data.
 */
import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { RULE_TYPE_LABELS } from './RuleBadges';
import type {
  RuleListItem,
  RuleSeverity,
  RuleStatus,
  RuleType,
} from '../../types/rules';

export interface FilterState {
  statuses: RuleStatus[];
  severities: RuleSeverity[];
  ruleTypes: RuleType[];
  domains: string[];
  tags: string[];
}

export const DEFAULT_FILTER: FilterState = {
  statuses: ['active'],
  severities: [],
  ruleTypes: [],
  domains: [],
  tags: [],
};

const ALL_STATUSES: RuleStatus[] = ['active', 'proposed', 'draft', 'retired'];
const ALL_SEVERITIES: RuleSeverity[] = ['must', 'should', 'suggested'];
const ALL_RULE_TYPES: RuleType[] = [
  'trigger',
  'quantity_heuristic',
  'companion',
  'validation',
  'activity_resolution',
];

interface RuleFilterSidebarProps {
  rules: RuleListItem[];
  value: FilterState;
  onChange: (_next: FilterState) => void;
  onClear: () => void;
  className?: string;
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

const RuleFilterSidebar: React.FC<RuleFilterSidebarProps> = ({
  rules,
  value,
  onChange,
  onClear,
  className,
}) => {
  const allDomains = React.useMemo(
    () => uniqueSorted(rules.flatMap((r) => r.domains)),
    [rules],
  );
  const allTags = React.useMemo(
    () => uniqueSorted(rules.flatMap((r) => r.tags)),
    [rules],
  );

  const isAtDefault =
    value.statuses.length === 1 &&
    value.statuses[0] === 'active' &&
    value.severities.length === 0 &&
    value.ruleTypes.length === 0 &&
    value.domains.length === 0 &&
    value.tags.length === 0;

  return (
    <aside
      className={cn(
        'w-64 shrink-0 space-y-5 rounded-lg border bg-white p-4 shadow-sm',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Filters</h2>
        <button
          type="button"
          onClick={onClear}
          disabled={isAtDefault}
          className={cn(
            'inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground',
            isAtDefault && 'cursor-not-allowed opacity-40',
          )}
        >
          <X className="h-3 w-3" /> Clear all
        </button>
      </div>

      <FilterGroup
        label="Status"
        options={ALL_STATUSES}
        selected={value.statuses}
        formatLabel={(s) => s}
        onToggle={(s) => onChange({ ...value, statuses: toggle(value.statuses, s) })}
      />

      <FilterGroup
        label="Severity"
        options={ALL_SEVERITIES}
        selected={value.severities}
        formatLabel={(s) => s}
        onToggle={(s) =>
          onChange({ ...value, severities: toggle(value.severities, s) })
        }
      />

      <FilterGroup
        label="Rule type"
        options={ALL_RULE_TYPES}
        selected={value.ruleTypes}
        formatLabel={(t) => RULE_TYPE_LABELS[t] || t}
        onToggle={(t) =>
          onChange({ ...value, ruleTypes: toggle(value.ruleTypes, t) })
        }
      />

      <FilterGroup
        label="Domains"
        options={allDomains}
        selected={value.domains}
        formatLabel={(d) => d}
        onToggle={(d) => onChange({ ...value, domains: toggle(value.domains, d) })}
        emptyHint="No domains in this set"
        scrollable
      />

      <FilterGroup
        label="Tags"
        options={allTags}
        selected={value.tags}
        formatLabel={(t) => t}
        onToggle={(t) => onChange({ ...value, tags: toggle(value.tags, t) })}
        emptyHint="No tags in this set"
        scrollable
      />
    </aside>
  );
};

interface FilterGroupProps<T extends string> {
  label: string;
  options: T[];
  selected: T[];
  formatLabel: (_option: T) => string;
  onToggle: (_option: T) => void;
  emptyHint?: string;
  scrollable?: boolean;
}

function FilterGroup<T extends string>({
  label,
  options,
  selected,
  formatLabel,
  onToggle,
  emptyHint,
  scrollable,
}: FilterGroupProps<T>): React.ReactElement {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {options.length === 0 ? (
        <div className="text-xs italic text-muted-foreground">
          {emptyHint || 'No options.'}
        </div>
      ) : (
        <div
          className={cn(
            'space-y-1',
            scrollable && 'max-h-40 overflow-y-auto pr-1',
          )}
        >
          {options.map((opt) => {
            const checked = selected.includes(opt);
            return (
              <label
                key={opt}
                className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded"
                  checked={checked}
                  onChange={() => onToggle(opt)}
                />
                <span className={cn(checked && 'font-medium text-slate-900')}>
                  {formatLabel(opt)}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RuleFilterSidebar;
